import classNames from "classnames";
import { useTranslation } from "react-i18next";

import { TestProps } from "shared/types";
import { cloneObject } from "shared/utils";

import { DEFAULT_FILTER_TYPE } from "features/ui/Filters/constants";
import { FilterOperator } from "features/ui/Filters/types";
import { getFirstAvailableSelectedOperator } from "features/ui/Filters/utils";
import { SchemaEntry } from "features/ui/MaterialTable";

import { DEFAULT_FILTER_BUILDER_STATE } from "./constants";
import styles from "./FilterBuilder.module.css";
import FilterGroup from "./FilterGroup";
import { AnyAll } from "./FilterGroup/AnyAllSelect";
import { EditableRowProperty } from "./FilterRow/types";
import { FilterGroupState, FilterRowState } from "./types";
import {
  deleteFromGroupByID,
  getFilterRowRelations,
  insertInGroupAfterID,
  updateGroupAnyAllByID,
  updateRowPropertyByID,
} from "./utils";

interface Props extends TestProps {
  filterBuilderState?: FilterGroupState;
  filtersSchema: SchemaEntry<string>[];
  defaultFilters?: FilterGroupState;
  onChange: (filterState: FilterGroupState) => void;

  inline?: boolean;
  disabled?: boolean;
  attributePlaceholder?: string;
  // embedded is a parameter we send to FilterSelector to say "this will be embedded as part of another filter selector",
  // which would adjust the paddings and coloring. This is applicable to Exists and Occurs filters
  embedded?: boolean;
  hasChanges?: boolean;
  occursFilterSelector?: boolean;
  hasTopLevelActions?: boolean;
  hasInitialFocus?: boolean;
  disableGroups?: boolean;
}

const FilterBuilder = ({
  filterBuilderState = DEFAULT_FILTER_BUILDER_STATE,
  filtersSchema,
  defaultFilters,
  onChange,
  inline = false,
  embedded,
  disabled,
  attributePlaceholder,
  testId = "filter-builder",
  hasChanges,
  occursFilterSelector,
  hasTopLevelActions = true,
  hasInitialFocus = true,
  disableGroups = false,
}: Props) => {
  const { t } = useTranslation("filters");

  const addNewRow = (id: string) => {
    onChange(insertInGroupAfterID(filterBuilderState, id, "row"));
  };

  const addNewGroup = (id: string) => {
    const updated = insertInGroupAfterID(filterBuilderState, id, "group");
    onChange(updated);
  };

  const onDelete = (id: string) => {
    const relations = getFilterRowRelations(filterBuilderState, id);
    if (!relations) return;

    const { siblingBefore, siblingAfter, parent } = relations;
    let gotoRow: HTMLElement | null = null;

    if (!siblingBefore && !siblingAfter) {
      // if no siblings, let the default autofocus happen
      onChange(deleteFromGroupByID(filterBuilderState, id));

      return;
    }

    if (siblingBefore) {
      gotoRow = document.getElementById(siblingBefore);
    } else if (siblingAfter) {
      gotoRow = document.getElementById(siblingAfter);
    } else if (parent) {
      gotoRow = document.getElementById(parent);
    }

    if (gotoRow) {
      setTimeout(() => {
        const firstInput = gotoRow.querySelector("input");
        if (firstInput) {
          firstInput.focus();
        }
      }, 0);
    }

    onChange(deleteFromGroupByID(filterBuilderState, id));
  };

  const onGroupOperatorChange = (id: string, anyAll: AnyAll) => {
    onChange(updateGroupAnyAllByID(filterBuilderState, id, anyAll));
  };

  const onResetToDefault = () => {
    onChange(defaultFilters || DEFAULT_FILTER_BUILDER_STATE);
  };

  const onClear = () => {
    onChange(DEFAULT_FILTER_BUILDER_STATE);
  };

  const onRowDataChange = (
    id: string,
    property: EditableRowProperty,
    values?: string | string[],
    filterValues?: FilterRowState["values"]
  ) => {
    let newFilterState = cloneObject(filterBuilderState);

    // if we are changing the attribute, we need to reset operator and values for this id before applying this change
    if (property === "attribute") {
      // a row's attribute holds the filter's fieldName, which is not always the
      // schema entry's accessor (e.g. smart groups)
      const attributeSchema = filtersSchema.find(
        (schemaEntry) => schemaEntry.filter?.fieldName === values
      );

      const defaultOperator = getFirstAvailableSelectedOperator(
        t,
        null,
        attributeSchema?.filter?.filterType || DEFAULT_FILTER_TYPE,
        attributeSchema
      );

      newFilterState = updateRowPropertyByID(
        newFilterState,
        id,
        "operator",
        defaultOperator
      );

      newFilterState = updateRowPropertyByID(newFilterState, id, "values", []);
    }

    // if operator is is_empty or is_not_empty, we need to set values to "null"
    if (
      property === "operator" &&
      (values === FilterOperator.IS_EMPTY ||
        values === FilterOperator.IS_NOT_EMPTY)
    ) {
      newFilterState = updateRowPropertyByID(newFilterState, id, "values", [
        "null",
      ]);
    }

    if (property === "operator" && values === FilterOperator.IN) {
      newFilterState = updateRowPropertyByID(newFilterState, id, "values", []);
    }

    // if operator is is_true or is_false, we need to set values to "true" or "false"
    if (
      property === "operator" &&
      (values === FilterOperator.IS_TRUE || values === FilterOperator.IS_FALSE)
    ) {
      newFilterState = updateRowPropertyByID(newFilterState, id, "values", [
        String(values === FilterOperator.IS_TRUE),
      ]);
    }

    // single-value operators keep only the first value (e.g. switching from "between")
    if (
      property === "operator" &&
      (values === FilterOperator.GREATER_THAN ||
        values === FilterOperator.GREATER_OR_EQUAL ||
        values === FilterOperator.LESS_THAN ||
        values === FilterOperator.LESS_OR_EQUAL)
    ) {
      newFilterState = updateRowPropertyByID(
        newFilterState,
        id,
        "values",
        filterValues?.slice(0, 1) ?? []
      );
    }

    onChange(updateRowPropertyByID(newFilterState, id, property, values));
  };

  return (
    <div
      data-testid={testId}
      className={classNames(styles.filterBuilder, "w-full", {
        "border-t border-gray-300 h-[35rem] pt-2": !inline,
        "-mt-4": embedded,
      })}
    >
      <FilterGroup
        key={filterBuilderState.id}
        id={filterBuilderState.id}
        depth={0}
        children={filterBuilderState.children}
        anyAll={filterBuilderState.anyAll}
        onNewRow={addNewRow}
        onNewGroup={addNewGroup}
        onDelete={onDelete}
        onGroupOperatorChange={onGroupOperatorChange}
        onRowDataChange={onRowDataChange}
        schema={filtersSchema}
        onResetToDefault={onResetToDefault}
        onClear={onClear}
        disabled={disabled}
        attributePlaceholder={attributePlaceholder}
        embedded={embedded}
        hasChanges={hasChanges}
        occursFilterSelector={occursFilterSelector}
        hasTopLevelActions={hasTopLevelActions}
        hasInitialFocus={hasInitialFocus}
        disableGroups={disableGroups}
      />
      {/* Handle for focus to not get lost while tabbing */}

      <button
        className="sr-only"
        tabIndex={0}
        onFocus={() => {
          // the id of the first row is always "group-0"
          const item = document.getElementById("group-0");
          if (item) {
            setTimeout(() => {
              const firstInput = item.querySelector("input");
              if (firstInput) {
                firstInput.focus();
              }
            }, 0);
          }
        }}
        aria-label={t("return_to_first_input")}
      />
    </div>
  );
};

export default FilterBuilder;
